import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area, ComposedChart } from "recharts";
import { ChartBar, Users, Clock, CalendarBlank, TrendUp, Table, ChartLine, Crown, ArrowUp, ArrowDown } from "@phosphor-icons/react";

const PIE_COLORS = ["#81B29A", "#E07A5F", "#F4A261", "#9CA3AF"];

const TIER_COLORS = { gold: "#FFB800", silver: "#A8A8A8", bronze: "#CD7F32", none: "#D6D3D1" };

function PeakHoursHeatmap({ data }) {
  const { cells, peak_hour, peak_day, peak_covers } = data;
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const hours = [];
  for (let h = 10; h <= 23; h++) {
    hours.push({ value: h, label: `${h % 12 || 12}${h < 12 ? "a" : "p"}` });
  }

  // Build lookup
  const lookup = {};
  let maxCovers = 0;
  cells.forEach((c) => {
    const key = `${c.day}-${c.hour}`;
    lookup[key] = c;
    if (c.covers > maxCovers) maxCovers = c.covers;
  });

  const getColor = (covers) => {
    if (!covers || covers === 0) return "#F5F5F4";
    const intensity = Math.min(covers / Math.max(maxCovers, 1), 1);
    if (intensity > 0.75) return "#2F4F4F";
    if (intensity > 0.5) return "#3D6B6B";
    if (intensity > 0.25) return "#81B29A";
    return "#C8E6D8";
  };

  const getTextColor = (covers) => {
    if (!covers || covers === 0) return "#D6D3D1";
    const intensity = Math.min(covers / Math.max(maxCovers, 1), 1);
    return intensity > 0.5 ? "#fff" : "#44403C";
  };

  return (
    <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="peak-hours-heatmap">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 flex items-center gap-2">
          <Clock size={16} weight="duotone" style={{ color: "#E07A5F" }} /> Peak Hours Heatmap
        </h3>
        {peak_day && peak_hour !== null && (
          <span className="text-xs text-stone-500">
            Busiest: <span className="font-semibold text-stone-800">{peak_day} {peak_hour % 12 || 12}{peak_hour < 12 ? "AM" : "PM"}</span>
            <span className="ml-1 text-stone-400">({peak_covers} covers)</span>
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[600px]">
          {/* Header row */}
          <div className="flex items-center gap-0.5 mb-0.5">
            <div className="w-10 flex-shrink-0" />
            {hours.map((h) => (
              <div key={h.value} className="flex-1 text-center text-[10px] text-stone-400 font-medium pb-1">{h.label}</div>
            ))}
          </div>
          {/* Grid rows */}
          {days.map((dayName, dayIdx) => (
            <div key={dayName} className="flex items-center gap-0.5 mb-0.5">
              <div className="w-10 flex-shrink-0 text-[11px] font-semibold text-stone-500 text-right pr-2">{dayName}</div>
              {hours.map((h) => {
                const cell = lookup[`${dayIdx}-${h.value}`];
                const covers = cell?.covers || 0;
                const count = cell?.count || 0;
                return (
                  <div
                    key={h.value}
                    className="flex-1 aspect-[1.8] rounded-[3px] flex items-center justify-center cursor-default transition-all hover:scale-110 hover:z-10 relative group"
                    style={{ backgroundColor: getColor(covers), minHeight: "28px" }}
                    data-testid={`heatmap-${dayName}-${h.value}`}
                    title={`${dayName} ${h.value % 12 || 12}${h.value < 12 ? "AM" : "PM"}: ${count} bookings, ${covers} covers`}
                  >
                    {covers > 0 && (
                      <span className="text-[10px] font-bold" style={{ color: getTextColor(covers) }}>{covers}</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          {/* Legend */}
          <div className="flex items-center justify-end gap-2 mt-3">
            <span className="text-[10px] text-stone-400">Less</span>
            {["#F5F5F4", "#C8E6D8", "#81B29A", "#3D6B6B", "#2F4F4F"].map((c, i) => (
              <div key={i} className="w-4 h-4 rounded-[2px]" style={{ backgroundColor: c }} />
            ))}
            <span className="text-[10px] text-stone-400">More</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function HistoricalView({ data, days, setDays, heatmapData }) {
  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-stone-500 text-sm">Loading historical data...</p>
      </div>
    );
  }

  const { daily, server_rankings, aggregate } = data;

  if (daily.length === 0) {
    return (
      <div className="text-center py-16" data-testid="historical-empty">
        <ChartLine size={48} className="mx-auto mb-4 text-stone-300" />
        <h3 className="text-lg font-semibold text-stone-700 mb-2" style={{ fontFamily: "Work Sans" }}>No Historical Data Yet</h3>
        <p className="text-sm text-stone-500 max-w-md mx-auto">
          Historical trends populate automatically each time you complete an End of Service. Run a few service days and come back here to see your restaurant's performance over time.
        </p>
      </div>
    );
  }

  const agg = aggregate || {};

  // Compute comparison: last day vs average
  const lastDay = daily[daily.length - 1] || {};
  const coversChange = agg.avg_covers_per_day > 0 ? Math.round(((lastDay.total_covers - agg.avg_covers_per_day) / agg.avg_covers_per_day) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="historical-view">
      {/* Period Selector */}
      <div className="flex justify-end gap-1">
        {[7, 14, 30].map((d) => (
          <button
            key={d}
            className={`text-xs px-3 py-1.5 rounded-md transition-all font-medium ${days === d ? "bg-[#2F4F4F] text-white" : "bg-stone-100 text-stone-500 hover:bg-stone-200"}`}
            onClick={() => setDays(d)}
            data-testid={`snapshot-days-${d}`}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* Aggregate Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="aggregate-cards">
        <div className="bg-white rounded-lg border border-stone-200 p-4 text-center">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Service Days</p>
          <p className="text-2xl font-bold mt-1" style={{ fontFamily: "Work Sans", color: "#2F4F4F" }}>{agg.total_service_days}</p>
        </div>
        <div className="bg-white rounded-lg border border-stone-200 p-4 text-center">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Total Covers</p>
          <p className="text-2xl font-bold mt-1" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>{agg.total_covers}</p>
        </div>
        <div className="bg-white rounded-lg border border-stone-200 p-4 text-center">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Avg / Day</p>
          <p className="text-2xl font-bold mt-1" style={{ fontFamily: "Work Sans", color: "#81B29A" }}>{agg.avg_covers_per_day}</p>
        </div>
        <div className="bg-white rounded-lg border border-stone-200 p-4 text-center">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Reservations</p>
          <p className="text-2xl font-bold mt-1" style={{ fontFamily: "Work Sans", color: "#F4A261" }}>{agg.total_reservations}</p>
        </div>
        <div className="bg-white rounded-lg border border-stone-200 p-4 text-center">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Walk-Ins</p>
          <p className="text-2xl font-bold mt-1" style={{ fontFamily: "Work Sans", color: "#E07A5F" }}>{agg.total_walkins}</p>
        </div>
        <div className="bg-white rounded-lg border border-stone-200 p-4 text-center">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Avg Turn</p>
          <p className="text-2xl font-bold mt-1" style={{ fontFamily: "Work Sans", color: "#2F4F4F" }}>{agg.avg_turn_minutes > 0 ? `${agg.avg_turn_minutes}m` : "--"}</p>
        </div>
      </div>

      {/* Covers Per Day — Area Chart */}
      <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="covers-chart">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 flex items-center gap-2">
            <Users size={16} weight="duotone" style={{ color: "#2F4F4F" }} /> Covers Per Day
          </h3>
          {coversChange !== 0 && (
            <span className={`text-xs font-medium flex items-center gap-0.5 ${coversChange > 0 ? "text-green-600" : "text-red-500"}`}>
              {coversChange > 0 ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />}
              {Math.abs(coversChange)}% vs avg
            </span>
          )}
        </div>
        <div className="h-[240px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={daily}>
              <defs>
                <linearGradient id="coversFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2F4F4F" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#2F4F4F" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#57534E" }} />
              <YAxis tick={{ fontSize: 11, fill: "#57534E" }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #E7E5E4", fontSize: "12px" }} />
              <Area type="monotone" dataKey="total_covers" stroke="#2F4F4F" strokeWidth={2} fill="url(#coversFill)" name="Covers" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Reservations vs Walk-ins Stacked Bar */}
        <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="res-walkins-chart">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
            <CalendarBlank size={16} weight="duotone" /> Reservations vs Walk-Ins
          </h3>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#57534E" }} />
                <YAxis tick={{ fontSize: 11, fill: "#57534E" }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #E7E5E4", fontSize: "12px" }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: "11px" }} />
                <Bar dataKey="total_reservations" stackId="a" fill="#2F4F4F" name="Reservations" radius={[0, 0, 0, 0]} />
                <Bar dataKey="walk_ins_seated" stackId="a" fill="#81B29A" name="Walk-Ins" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Avg Turn Time Trend */}
        <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="turn-time-chart">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
            <Clock size={16} weight="duotone" /> Avg Turn Time
          </h3>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#57534E" }} />
                <YAxis tick={{ fontSize: 11, fill: "#57534E" }} unit="m" allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #E7E5E4", fontSize: "12px" }} formatter={(v) => `${v}m`} />
                <Line type="monotone" dataKey="avg_turn_minutes" stroke="#F4A261" strokeWidth={2.5} dot={{ r: 4, fill: "#F4A261" }} name="Avg Turn" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Service Duration Trend */}
      <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="duration-chart">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
          <TrendUp size={16} weight="duotone" /> Service Duration
        </h3>
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#57534E" }} />
              <YAxis tick={{ fontSize: 11, fill: "#57534E" }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #E7E5E4", fontSize: "12px" }} formatter={(v, name) => name === "Duration" ? `${Math.floor(v / 60)}h ${v % 60}m` : v} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: "11px" }} />
              <Bar dataKey="total_turns" fill="#81B29A" name="Table Turns" radius={[3, 3, 0, 0]} barSize={20} />
              <Line type="monotone" dataKey="service_duration_minutes" stroke="#E07A5F" strokeWidth={2} dot={{ r: 3 }} name="Duration" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Guest Insights */}
      <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="guest-insights-chart">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
          <Users size={16} weight="duotone" /> Guest Insights
        </h3>
        <div className="h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#57534E" }} />
              <YAxis tick={{ fontSize: 11, fill: "#57534E" }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #E7E5E4", fontSize: "12px" }} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: "11px" }} />
              <Bar dataKey="returning_guests" fill="#2F4F4F" name="Returning" radius={[0, 0, 0, 0]} stackId="g" />
              <Bar dataKey="new_guests" fill="#81B29A" name="New Guests" radius={[4, 4, 0, 0]} stackId="g" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Server Rankings */}
      {server_rankings && server_rankings.length > 0 && (
        <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="server-rankings">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
            <Crown size={16} weight="duotone" style={{ color: "#FFB800" }} /> Top Servers ({days} Day Period)
          </h3>
          <div className="space-y-2">
            {server_rankings.map((srv, idx) => {
              const maxCovers = server_rankings[0]?.total_covers || 1;
              const barWidth = Math.max(5, (srv.total_covers / maxCovers) * 100);
              return (
                <div key={srv.server_name} className="flex items-center gap-3 p-3 rounded-lg border border-stone-100 hover:bg-stone-50 transition-all" data-testid={`ranking-${idx}`}>
                  <div className="w-7 text-center flex-shrink-0">
                    <span className="font-mono font-bold text-lg" style={{ color: idx === 0 ? "#FFB800" : idx === 1 ? "#A8A8A8" : idx === 2 ? "#CD7F32" : "#52525B" }}>
                      {idx + 1}
                    </span>
                  </div>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: srv.color }}>
                    {srv.server_name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-stone-800">{srv.server_name}</span>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="font-mono font-semibold" style={{ color: "#81B29A" }}>{srv.total_covers} <span className="text-stone-500 font-normal">covers</span></span>
                        <span className="font-mono" style={{ color: "#2F4F4F" }}>{srv.total_turns} <span className="text-stone-500 font-normal">turns</span></span>
                        <span className="font-mono text-stone-400">{srv.days_worked}d</span>
                      </div>
                    </div>
                    <div className="w-full h-1 rounded-full bg-stone-100">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${barWidth}%`, backgroundColor: srv.color }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Peak Hours Heatmap */}
      {heatmapData && heatmapData.cells && heatmapData.cells.length > 0 && (
        <PeakHoursHeatmap data={heatmapData} />
      )}
    </div>
  );
}


export default function AnalyticsPage() {
  const [activeTab, setActiveTab] = useState("today");
  const [overview, setOverview] = useState(null);
  const [hourly, setHourly] = useState([]);
  const [serverLoad, setServerLoad] = useState([]);
  const [trends, setTrends] = useState([]);
  const [trendPeriod, setTrendPeriod] = useState("week");
  const [tablePerf, setTablePerf] = useState([]);
  const [dailySummary, setDailySummary] = useState(null);
  const [waitEstimate, setWaitEstimate] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [snapshotData, setSnapshotData] = useState(null);
  const [snapshotDays, setSnapshotDays] = useState(30);
  const [heatmapData, setHeatmapData] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [ov, hr, sl, ds, we, lb] = await Promise.all([
        api.get("/analytics/overview"),
        api.get("/analytics/hourly"),
        api.get("/analytics/server-load"),
        api.get("/analytics/daily-summary"),
        api.get("/analytics/wait-estimate?party_size=2"),
        api.get("/analytics/leaderboard"),
      ]);
      setOverview(ov.data);
      setHourly(hr.data);
      setServerLoad(sl.data);
      setDailySummary(ds.data);
      setWaitEstimate(we.data);
      setLeaderboard(lb.data);
    } catch { toast.error("Failed to load analytics"); }
  }, []);

  const fetchTrends = useCallback(async () => {
    try {
      const { data } = await api.get(`/analytics/trends?period=${trendPeriod}`);
      setTrends(data);
    } catch { /* silent */ }
  }, [trendPeriod]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);
  useEffect(() => { fetchTrends(); }, [fetchTrends]);

  const fetchSnapshotTrends = useCallback(async () => {
    try {
      const { data } = await api.get(`/analytics/snapshot-trends?days=${snapshotDays}`);
      setSnapshotData(data);
    } catch { /* silent */ }
  }, [snapshotDays]);

  useEffect(() => { if (activeTab === "historical") fetchSnapshotTrends(); }, [activeTab, fetchSnapshotTrends]);

  const fetchHeatmap = useCallback(async () => {
    try {
      const { data } = await api.get(`/analytics/peak-hours?days=${snapshotDays}`);
      setHeatmapData(data);
    } catch { /* silent */ }
  }, [snapshotDays]);

  useEffect(() => { if (activeTab === "historical") fetchHeatmap(); }, [activeTab, fetchHeatmap]);

  useEffect(() => {
    const fetchTablePerf = async () => {
      try {
        const { data } = await api.get("/analytics/table-performance?days=7");
        setTablePerf(data);
      } catch { /* silent */ }
    };
    fetchTablePerf();
  }, []);

  if (!overview) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <p className="text-stone-500">Loading analytics...</p>
      </div>
    );
  }

  const statusData = [
    { name: "Available", value: overview.available, color: "#81B29A" },
    { name: "Occupied", value: overview.occupied, color: "#E07A5F" },
    { name: "Reserved", value: overview.reserved, color: "#F4A261" },
    { name: "Blocked", value: Math.max(0, overview.total_tables - overview.available - overview.occupied - overview.reserved), color: "#9CA3AF" },
  ].filter((d) => d.value > 0);

  const metrics = [
    { label: "Total Tables", value: overview.total_tables, icon: ChartBar, color: "#2F4F4F" },
    { label: "Occupancy Rate", value: `${overview.occupancy_rate}%`, icon: Users, color: "#E07A5F" },
    { label: "Today's Reservations", value: overview.today_reservations, icon: CalendarBlank, color: "#F4A261" },
    { label: "Guests Waiting", value: overview.waiting_guests, icon: Clock, color: "#81B29A" },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="analytics-page">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Analytics Dashboard</h2>
        <div className="flex gap-1 bg-stone-100 rounded-lg p-0.5" data-testid="analytics-tabs">
          <button
            className={`text-xs px-4 py-1.5 rounded-md transition-all font-medium ${activeTab === "today" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
            onClick={() => setActiveTab("today")}
            data-testid="tab-today"
          >
            Today
          </button>
          <button
            className={`text-xs px-4 py-1.5 rounded-md transition-all font-medium ${activeTab === "historical" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
            onClick={() => setActiveTab("historical")}
            data-testid="tab-historical"
          >
            <ChartLine size={13} className="inline mr-1" weight="bold" />
            Historical
          </button>
        </div>
      </div>

      {activeTab === "historical" ? (
        <HistoricalView data={snapshotData} days={snapshotDays} setDays={setSnapshotDays} heatmapData={heatmapData} />
      ) : (
      <>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {metrics.map((m, idx) => (
          <div key={m.label} className="bg-white rounded-lg border border-stone-200 p-5 animate-fade-in" style={{ animationDelay: `${idx * 80}ms` }} data-testid={`metric-${m.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{m.label}</span>
              <m.icon size={20} weight="duotone" style={{ color: m.color }} />
            </div>
            <span className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>{m.value}</span>
          </div>
        ))}
      </div>

      {/* Daily Summary + Wait Estimate */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {dailySummary && (
          <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="daily-summary">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
              <TrendUp size={16} weight="duotone" /> Today's Service Summary
            </h3>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center p-3 rounded-lg bg-[#2F4F4F]/5">
                <p className="text-2xl font-bold" style={{ color: "#2F4F4F", fontFamily: "Work Sans" }}>{dailySummary.total_covers}</p>
                <p className="text-[10px] text-stone-500 uppercase">Total Covers</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-[#F4A261]/10">
                <p className="text-2xl font-bold" style={{ color: "#F4A261", fontFamily: "Work Sans" }}>{dailySummary.total_reservations}</p>
                <p className="text-[10px] text-stone-500 uppercase">Reservations</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-[#E07A5F]/10">
                <p className="text-2xl font-bold" style={{ color: "#E07A5F", fontFamily: "Work Sans" }}>{dailySummary.total_walkins}</p>
                <p className="text-[10px] text-stone-500 uppercase">Walk-Ins</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs text-stone-500 mb-4">
              <span>Avg Party: <span className="font-semibold text-stone-700">{dailySummary.avg_party_size}</span></span>
              <span>Seated: <span className="font-semibold text-stone-700">{dailySummary.seated_count}</span></span>
              <span>Completed: <span className="font-semibold text-stone-700">{dailySummary.completed_count}</span></span>
              <span>Cancelled: <span className="font-semibold text-stone-700">{dailySummary.cancelled_count}</span></span>
            </div>
            {dailySummary.server_covers.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-2">Server Covers</p>
                <div className="space-y-1.5">
                  {dailySummary.server_covers.filter((s) => s.covers > 0 || s.turns > 0).map((s) => (
                    <div key={s.server_name} className="flex items-center gap-2 text-xs">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                      <span className="flex-1 font-medium text-stone-700">{s.server_name}</span>
                      <span className="text-stone-500">{s.covers} covers</span>
                      <span className="text-stone-500">{s.turns} turns</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {waitEstimate && (
          <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="wait-estimate">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
              <Clock size={16} weight="duotone" /> Wait Time Estimator
            </h3>
            <div className="text-center py-4">
              <p className="text-5xl font-bold" style={{ fontFamily: "Work Sans", color: waitEstimate.estimated_wait_min === 0 ? "#22C55E" : waitEstimate.estimated_wait_min <= 15 ? "#F4A261" : "#E07A5F" }}>
                {waitEstimate.estimated_wait_min === 0 ? "Now" : `${waitEstimate.estimated_wait_min}m`}
              </p>
              <p className="text-xs text-stone-500 mt-1">{waitEstimate.estimated_wait_min === 0 ? "Tables available" : "Estimated wait for party of 2"}</p>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="text-center p-2.5 rounded-lg bg-stone-50">
                <p className="text-lg font-bold" style={{ color: "#2F4F4F" }}>{waitEstimate.avg_turn_min}m</p>
                <p className="text-[10px] text-stone-500">Avg Turn Time</p>
              </div>
              <div className="text-center p-2.5 rounded-lg bg-stone-50">
                <p className="text-lg font-bold" style={{ color: "#81B29A" }}>{waitEstimate.available}</p>
                <p className="text-[10px] text-stone-500">Available Tables</p>
              </div>
              <div className="text-center p-2.5 rounded-lg bg-stone-50">
                <p className="text-lg font-bold" style={{ color: "#E07A5F" }}>{waitEstimate.occupied}</p>
                <p className="text-[10px] text-stone-500">Occupied</p>
              </div>
              <div className="text-center p-2.5 rounded-lg bg-stone-50">
                <p className="text-lg font-bold" style={{ color: "#F4A261" }}>{waitEstimate.waiting_ahead}</p>
                <p className="text-[10px] text-stone-500">Waiting Ahead</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Table Status Pie */}
        <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="table-status-chart">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4">Table Status</h3>
          {statusData.length === 0 ? (
            <p className="text-sm text-stone-500 text-center py-8">No tables yet</p>
          ) : (
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value" label={({ name, value }) => `${name} (${value})`} labelLine={false}>
                    {statusData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="flex justify-center gap-4 mt-2">
            {statusData.map((d) => (
              <div key={d.name} className="flex items-center gap-1.5 text-xs text-stone-500">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: d.color }} />
                {d.name}
              </div>
            ))}
          </div>
        </div>

        {/* Hourly Reservations Bar */}
        <div className="bg-white rounded-lg border border-stone-200 p-5 lg:col-span-2" data-testid="hourly-chart">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4">Today's Reservations by Hour</h3>
          {hourly.length === 0 ? (
            <p className="text-sm text-stone-500 text-center py-8">No reservation data for today</p>
          ) : (
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hourly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="hour" tick={{ fontSize: 11, fill: "#57534E" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#57534E" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #E7E5E4", fontSize: "12px" }} />
                  <Bar dataKey="reservations" fill="#2F4F4F" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Server Load */}
      {serverLoad.length > 0 && (
        <div className="mt-6 bg-white rounded-lg border border-stone-200 p-5" data-testid="server-load-chart">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4">Server Load</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {serverLoad.map((s, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 rounded-lg border border-stone-100">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: s.color }}>
                  {s.server_name.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-medium text-stone-800">{s.server_name}</p>
                  <p className="text-xs text-stone-500">{s.occupied_tables}/{s.total_tables} tables occupied</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Server Leaderboard */}
      {leaderboard.length > 0 && (
        <div className="mt-6 bg-white rounded-lg border border-stone-200 p-5" data-testid="server-leaderboard">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4">Server Leaderboard</h3>
          <div className="space-y-2">
            {leaderboard.map((s) => {
              const maxCovers = leaderboard[0]?.covers || 1;
              const barWidth = maxCovers > 0 ? Math.max(5, (s.covers / maxCovers) * 100) : 5;
              return (
                <div key={s.server_id} className="flex items-center gap-3 p-3 rounded-lg border border-stone-100 hover:bg-stone-50 transition-all" data-testid={`leaderboard-${s.server_id}`}>
                  <div className="w-7 text-center flex-shrink-0">
                    <span className="font-mono font-bold text-lg" style={{ color: s.rank === 1 ? "#FFB800" : s.rank === 2 ? "#A8A8A8" : s.rank === 3 ? "#CD7F32" : "#52525B" }}>
                      {s.rank}
                    </span>
                  </div>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: s.color }}>
                    {s.server_name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-stone-800">{s.server_name}</span>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="font-mono font-semibold" style={{ color: "#81B29A" }}>{s.covers} <span className="text-stone-500 font-normal">covers</span></span>
                        <span className="font-mono" style={{ color: "#2F4F4F" }}>{s.turns} <span className="text-stone-500 font-normal">turns</span></span>
                      </div>
                    </div>
                    <div className="w-full h-1 rounded-full bg-white/5">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${barWidth}%`, backgroundColor: s.color }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Trend Charts */}
      <div className="mt-6 bg-white rounded-lg border border-stone-200 p-5" data-testid="trend-chart">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendUp size={18} weight="duotone" style={{ color: "#2F4F4F" }} />
            <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500">Guest Trends</h3>
          </div>
          <div className="flex gap-1">
            {["week", "month"].map((p) => (
              <button
                key={p}
                className={`text-xs px-3 py-1.5 rounded-md transition-all font-medium ${trendPeriod === p ? "bg-[#2F4F4F] text-white" : "bg-stone-100 text-stone-500 hover:bg-stone-200"}`}
                onClick={() => setTrendPeriod(p)}
                data-testid={`trend-${p}-btn`}
              >
                {p === "week" ? "7 Days" : "30 Days"}
              </button>
            ))}
          </div>
        </div>
        {trends.length === 0 ? (
          <p className="text-sm text-stone-500 text-center py-8">No trend data available yet. Trends populate as reservations and walk-ins are recorded.</p>
        ) : (
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#57534E" }} interval={trendPeriod === "month" ? 4 : 0} />
                <YAxis tick={{ fontSize: 11, fill: "#57534E" }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #E7E5E4", fontSize: "12px" }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: "11px" }} />
                <Line type="monotone" dataKey="reservations" stroke="#2F4F4F" strokeWidth={2} dot={{ r: 3 }} name="Reservations" />
                <Line type="monotone" dataKey="walk_ins" stroke="#81B29A" strokeWidth={2} dot={{ r: 3 }} name="Walk-ins" />
                <Line type="monotone" dataKey="cancelled" stroke="#E07A5F" strokeWidth={2} dot={{ r: 3 }} name="Cancelled" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Table Performance */}
      {tablePerf.length > 0 && (
        <div className="mt-6 bg-white rounded-lg border border-stone-200 p-5" data-testid="table-performance">
          <div className="flex items-center gap-2 mb-4">
            <Table size={18} weight="duotone" style={{ color: "#2F4F4F" }} />
            <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500">Table Performance (7d)</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="text-left py-2 px-2 text-xs uppercase tracking-widest text-stone-500 font-semibold">Table</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-widest text-stone-500 font-semibold">Seats</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-widest text-stone-500 font-semibold">Avg Turn</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-widest text-stone-500 font-semibold">Utilization</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-widest text-stone-500 font-semibold">Turns</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-widest text-stone-500 font-semibold">Covers</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-widest text-stone-500 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {tablePerf.map((t) => (
                  <tr key={t.table_id} className="border-b border-stone-100 hover:bg-stone-50 transition-colors" data-testid={`perf-row-${t.table_name}`}>
                    <td className="py-2.5 px-2 font-medium text-stone-800">{t.table_name}</td>
                    <td className="py-2.5 px-2 text-center text-stone-600">{t.seats}</td>
                    <td className="py-2.5 px-2 text-center font-medium" style={{ color: "#2F4F4F" }}>
                      {t.avg_turn_minutes > 0 ? `${t.avg_turn_minutes}m` : "--"}
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold" style={{
                        backgroundColor: t.utilization_percent > 50 ? "#E07A5F20" : t.utilization_percent > 20 ? "#F4A26120" : "#81B29A20",
                        color: t.utilization_percent > 50 ? "#E07A5F" : t.utilization_percent > 20 ? "#F4A261" : "#81B29A",
                      }}>
                        {t.utilization_percent}%
                      </span>
                    </td>
                    <td className="py-2.5 px-2 text-center text-stone-600">{t.total_turns}</td>
                    <td className="py-2.5 px-2 text-center text-stone-600">{t.total_covers}</td>
                    <td className="py-2.5 px-2 text-center">
                      <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.status === "available" ? "#81B29A" : t.status === "occupied" ? "#E07A5F" : t.status === "reserved" ? "#F4A261" : "#9CA3AF" }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
